import { useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import api from "@/lib/api";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { TrendingUp, Upload } from "lucide-react";
import SalesExplorer from "@/components/sales/SalesExplorer";
import SalesScorecard from "@/components/sales/SalesScorecard";
import PeriodPicker from "@/components/sales/PeriodPicker";
import ImportDialog from "@/components/sales/ImportDialog";
import { PageHeader, UpdatedThroughChip } from "@/components/layout/PageHeader";
import { OPS } from "@/constants/testIds";
import { localeDate } from "@/lib/format";

// Sales-Explorer group_by -> the section permission it draws from.
const GROUP_SECTION = {
    day: "daily",
    month: "monthly",
    item: "items",
    branch: "branch_performance",
    channel: "channels",
};

export default function SalesPage() {
    const { me, isSuperAdmin } = useAuth();
    const { t, tn, lang } = useI18n();
    const [branch, setBranch] = useState("all");
    const [period, setPeriod] = useState({ start: null, end: null, label: null });
    const [meta, setMeta] = useState(null);
    const [importOpen, setImportOpen] = useState(false);
    const [refreshSignal, setRefreshSignal] = useState(0);
    const defaultedRef = useRef(false);

    useEffect(() => {
        let active = true;
        api
            .get("/sales/meta")
            .then((r) => active && setMeta(r.data))
            .catch(() => active && setMeta(null));
        return () => {
            active = false;
        };
    }, [refreshSignal]);

    // Default the period ONCE to the last 30 days of coverage (Sales Explorer
    // default). Guarded so it never overrides a user's later selection.
    useEffect(() => {
        if (defaultedRef.current) return;
        if (!meta?.has_data || !meta?.coverage_to) return;
        defaultedRef.current = true;
        const toIso = meta.coverage_to;
        const toD = new Date(toIso + "T00:00:00");
        const fromD = new Date(toD);
        fromD.setDate(fromD.getDate() - 29);
        const pad = (n) => String(n).padStart(2, "0");
        let startIso = `${fromD.getFullYear()}-${pad(fromD.getMonth() + 1)}-${pad(fromD.getDate())}`;
        if (meta.coverage_from && startIso < meta.coverage_from) startIso = meta.coverage_from;
        setPeriod({ start: startIso, end: toIso, label: t("exp_period_30") });
    }, [meta, t]);

    const branches = me?.sales?.branches || [];
    const permittedSections = useMemo(
        () => new Set((me?.sales?.sections || []).map((s) => s.key)),
        [me]
    );
    const explorerGroups = useMemo(() => {
        const all = ["day", "month", "item", "branch", "channel"];
        if (isSuperAdmin) return all;
        return all.filter((g) => permittedSections.has(GROUP_SECTION[g]));
    }, [isSuperAdmin, permittedSections]);

    return (
        <div data-testid={OPS.sales.page} className="space-y-6">
            <PageHeader
                icon={TrendingUp}
                title={t("sales_title")}
                subtitle={t("sales_subtitle")}
                meta={
                    <UpdatedThroughChip
                        label={t("updated_through")}
                        value={meta?.updated_through ? localeDate(meta.updated_through, lang) : null}
                    />
                }
                primaryAction={
                    isSuperAdmin ? (
                        <Button
                            data-testid={OPS.sales.importBtn}
                            onClick={() => setImportOpen(true)}
                            variant="outline"
                            className="gap-2"
                        >
                            <Upload className="h-4 w-4" />
                            {t("import_sales")}
                        </Button>
                    ) : null
                }
                actions={
                    <>
                        {meta?.has_data ? (
                            <PeriodPicker meta={meta} value={period} onChange={setPeriod} />
                        ) : null}
                        <Select value={branch} onValueChange={setBranch}>
                            <SelectTrigger
                                data-testid={OPS.sales.branchFilter}
                                className="lg:w-[220px]"
                            >
                                <SelectValue placeholder={t("branch_filter")} />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">{t("all_branches")}</SelectItem>
                                {branches.map((b) => (
                                    <SelectItem key={b.id} value={b.id}>
                                        {tn(b)}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </>
                }
            />

            {explorerGroups.length === 0 ? (
                <div className="rounded-xl surface border border-[color:var(--border-soft)] p-10 text-center text-[color:var(--ink-muted)]">
                    {t("no_access_desc")}
                </div>
            ) : (
                <>
                    {meta?.has_data && (isSuperAdmin || explorerGroups.includes("branch") || explorerGroups.includes("month")) ? (
                        <SalesScorecard meta={meta} />
                    ) : null}
                    <SalesExplorer
                        branch={branch}
                        start={period.start}
                        end={period.end}
                        groups={explorerGroups}
                        refreshKey={refreshSignal}
                    />
                </>
            )}

            {isSuperAdmin ? (
                <ImportDialog
                    open={importOpen}
                    onOpenChange={setImportOpen}
                    onIngested={() => setRefreshSignal((s) => s + 1)}
                />
            ) : null}
        </div>
    );
}
